import { toast, type ExternalToast } from 'sonner'
import { reportError } from '@/core/reporting/runtime'
import { createReportingDocumentShape } from '@/core/reporting/documentShape'
import { ReportingPointerType, ReportingStage } from '@/core/reporting/types'
import { useEditSession } from '@/core/store/editSession'

interface CaptureErrorOptions {
  stage?: ReportingStage
}

interface SurfaceErrorOptions extends CaptureErrorOptions {
  toastOptions?: ExternalToast
}

function currentPointerType(): ReportingPointerType | undefined {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
  return window.matchMedia('(pointer: coarse)').matches
    ? ReportingPointerType.Coarse
    : ReportingPointerType.Fine
}

export function captureError(
  error: unknown,
  fallbackMessage = 'Unknown application error',
  options: CaptureErrorOptions = {},
): Error {
  const normalized = error instanceof Error ? error : new Error(fallbackMessage, { cause: error })
  const state = useEditSession.getState()
  reportError(normalized, {
    stage: options.stage ?? ReportingStage.Application,
    toolId: state.activeTab,
    pointerType: currentPointerType(),
    documentShape: createReportingDocumentShape(state),
  })
  return normalized
}

export function surfaceError(
  error: unknown,
  fallbackMessage: string,
  options: SurfaceErrorOptions = {},
): Error {
  const normalized = captureError(error, fallbackMessage, options)
  toast.error(normalized.message, options.toastOptions)
  return normalized
}
